import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import LandingHeader from '../components/organisms/LandingHeader';
import Button from '../components/atoms/Button';
import Icon from '../components/atoms/Icon';
import { RESUME_TEMPLATES } from '../components/ResumeTemplates';
import CreditCostBadge from '../components/molecules/CreditCostBadge';
import useCredits from '../hooks/useCredits';
import SessionManager from '../utils/sessionManager';
import { API_BASE_URL } from '../config';

const EnhanceResumePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Credit system
  const { balance, hasEnoughCredits, getCost, deductCreditsForService } = useCredits();
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [isProfileComplete, setIsProfileComplete] = useState(true);
  const [profileData, setProfileData] = useState(null);
  
  const [formData, setFormData] = useState({
    resume: null,
    jobDescription: '',
    jobTitle: '',
    userPrompt: ''
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [enhancementResult, setEnhancementResult] = useState(null);
  const [status, setStatus] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('modern');
  const [loadingStep, setLoadingStep] = useState(0);
  const [resumePreviewUrl, setResumePreviewUrl] = useState(null);
  
  // Check profile completion on mount
  useEffect(() => {
    checkProfileCompletion();
  }, []);

  const checkProfileCompletion = async () => {
    const session = SessionManager.getCurrentSession();
    if (!session) return;

    try {
      const response = await fetch(`${API_BASE_URL}/candidates/${session.user_id}`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const profile = data.profile || data;
        setProfileData(profile);
        
        // Profile is complete if has full_name, phone, and at least one detail section
        const hasBasicInfo = profile.full_name && profile.phone;
        const hasDetails = (profile.education && profile.education.length > 0) ||
                          (profile.work_experience && profile.work_experience.length > 0) ||
                          (profile.skills && profile.skills.length > 0);
        
        setIsProfileComplete(hasBasicInfo && hasDetails);
      }
    } catch (error) {
      console.error('Error checking profile:', error);
    }
  };

  // Check if coming from Create Resume page
  useEffect(() => {
    if (location.state?.fromCreate && location.state?.resumeData) {
      const { resumeData, enhanceWithAI } = location.state;
      
      if (enhanceWithAI) {
        // Show loading animation and simulate AI enhancement
        setIsProcessing(true);
        setLoadingStep(0);
        
        const steps = [
          { delay: 500, step: 1 },
          { delay: 1500, step: 2 },
          { delay: 2500, step: 3 },
          { delay: 3500, step: 4 }
        ];

        steps.forEach(({ delay, step }) => {
          setTimeout(() => setLoadingStep(step), delay);
        });
        
        setTimeout(() => {
          setEnhancementResult({
            enhanced_resume: resumeData,
            changes: {
              added_skills: [],
              added_projects: [],
              enhanced_descriptions: [],
              added_keywords: []
            }
          });
          setStatus({
            type: 'success',
            title: 'Resume Created Successfully!',
            description: 'Your resume has been created and is ready to download.'
          });
          setIsProcessing(false);
          setLoadingStep(0);
        }, 4500);
      } else {
        // Skip AI, go directly to result
        setEnhancementResult({
          enhanced_resume: resumeData,
          changes: null
        });
        setStatus({
          type: 'success',
          title: 'Resume Created Successfully!',
          description: 'Your resume is ready to download.'
        });
      }
      
      // Clear location state
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);
  
  // Create Resume states
  const [showCreateResume, setShowCreateResume] = useState(false);
  const [createResumeStep, setCreateResumeStep] = useState(1);
  const [manualResumeData, setManualResumeData] = useState({
    personal_info: {
      name: '',
      email: '',
      phone: '',
      location: '',
      linkedin: ''
    },
    summary: '',
    experience: [],
    projects: [],
    skills: {
      technical: [],
      programming: [],
      tools: [],
      soft_skills: []
    },
    education: [],
    certifications: []
  });
  const [tempSkillInput, setTempSkillInput] = useState('');
  const [tempDescInput, setTempDescInput] = useState('');
  const [tempTechInput, setTempTechInput] = useState('');
  const [tempAchievementInput, setTempAchievementInput] = useState('');
  const [tempCourseworkInput, setTempCourseworkInput] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setStatus({
        type: 'error',
        title: 'Invalid File Type',
        description: 'Please upload a PDF file only.'
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setStatus({
        type: 'error',
        title: 'File Too Large',
        description: 'Please upload a file smaller than 5MB.'
      });
      return;
    }

    setFormData(prev => ({ ...prev, resume: file }));
    setStatus({
      type: 'success',
      title: 'Resume Selected',
      description: `${file.name} is ready for enhancement.`
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);

    // Require sign-in to actually enhance. Anonymous visitors can browse
    // the form, but when they hit Submit we bounce them to login with a
    // redirect back to this page so they can continue after signing in.
    const session = SessionManager.getCurrentSession();
    if (!session || !session.access_token) {
      navigate('/login?redirect=/enhance-resume');
      return;
    }

    if (!formData.resume || !formData.jobDescription.trim()) {
      setStatus({
        type: 'error',
        title: 'Missing Information',
        description: 'Please upload your resume and provide a job description.'
      });
      return;
    }

    // Check credits
    if (!hasEnoughCredits('resume_enhance')) {
      setShowCreditModal(true);
      return;
    }

    // Deduct credits
    const creditResult = await deductCreditsForService('resume_enhance');
    if (!creditResult.success) {
      setShowCreditModal(true);
      return;
    }

    setIsProcessing(true);
    setLoadingStep(0);

    // Create preview URL for the uploaded resume
    const fileUrl = URL.createObjectURL(formData.resume);
    setResumePreviewUrl(fileUrl);

    // Simulate loading steps
    const steps = [
      { delay: 500, step: 1 },
      { delay: 1500, step: 2 },
      { delay: 2500, step: 3 },
      { delay: 3500, step: 4 }
    ];

    steps.forEach(({ delay, step }) => {
      setTimeout(() => setLoadingStep(step), delay);
    });

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('resume', formData.resume);
      formDataToSend.append('job_description', formData.jobDescription);
      formDataToSend.append('job_title', formData.jobTitle);
      formDataToSend.append('user_prompt', formData.userPrompt);

      // NOTE: Do NOT set Content-Type here — the browser adds the correct
      // multipart/form-data boundary automatically for FormData bodies.
      // We must attach the Bearer token or the backend returns 401.
      const response = await fetch('https://aptirix-version-610696728606.asia-south1.run.app/api/candidates/enhance-resume', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`
        },
        body: formDataToSend
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to enhance resume');
      }

      const result = await response.json();
      setEnhancementResult(result);
      setStatus({
        type: 'success',
        title: 'Resume Enhanced Successfully!',
        description: 'Your resume has been enhanced based on the job description.'
      });

    } catch (error) {
      setStatus({
        type: 'error',
        title: 'Enhancement Failed',
        description: error.message || 'Failed to enhance resume. Please try again.'
      });
    } finally {
      setIsProcessing(false);
      setLoadingStep(0);
      if (resumePreviewUrl) {
        URL.revokeObjectURL(resumePreviewUrl);
        setResumePreviewUrl(null);
      }
    }
  };

  // Function to generate and download PDF automatically with selectable text
  const handleDownloadPDF = async () => {
    try {
      const resumeElement = document.getElementById('resume-content');
      if (!resumeElement) {
        console.error('Resume content not found');
        return;
      }

      // Get candidate name for filename
      const candidateName = enhancementResult?.enhanced_resume?.personal_info?.name || 'candidate';
      const fileName = `${candidateName.toLowerCase().replace(/\s+/g, '_')}_resume.pdf`;

      // Load jsPDF library dynamically if not already loaded
      if (!window.jspdf) {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
        script.onload = () => generateTextPDF(enhancementResult.enhanced_resume, fileName, selectedTemplate);
        document.head.appendChild(script);
      } else {
        generateTextPDF(enhancementResult.enhanced_resume, fileName, selectedTemplate);
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  // Create Resume Handlers
  const handleCreateResumeNext = () => {
    if (createResumeStep < 6) {
      setCreateResumeStep(createResumeStep + 1);
    }
  };

  const handleCreateResumeBack = () => {
    if (createResumeStep > 1) {
      setCreateResumeStep(createResumeStep - 1);
    }
  };

  const handleManualDataChange = (section, field, value) => {
    setManualResumeData(prev => ({
      ...prev,
      [section]: typeof prev[section] === 'object' && !Array.isArray(prev[section])
        ? { ...prev[section], [field]: value }
        : value
    }));
  };

  const handleAddArrayItem = (section, item) => {
    setManualResumeData(prev => ({
      ...prev,
      [section]: [...prev[section], item]
    }));
  };

  const handleRemoveArrayItem = (section, index) => {
    setManualResumeData(prev => ({
      ...prev,
      [section]: prev[section].filter((_, i) => i !== index)
    }));
  };

  const handleFinishCreateResume = async (enhanceWithAI = false) => {
    if (enhanceWithAI) {
      // Enhance with AI
      setIsProcessing(true);
      setLoadingStep(0);
      setShowCreateResume(false);

      const steps = [
        { delay: 500, step: 1 },
        { delay: 1500, step: 2 },
        { delay: 2500, step: 3 },
        { delay: 3500, step: 4 }
      ];

      steps.forEach(({ delay, step }) => {
        setTimeout(() => setLoadingStep(step), delay);
      });

      try {
        // Simulate AI enhancement (you can replace with actual API call)
        await new Promise(resolve => setTimeout(resolve, 4000));
        
        setEnhancementResult({
          enhanced_resume: manualResumeData,
          changes: {
            added_skills: [],
            added_projects: [],
            enhanced_descriptions: [],
            added_keywords: []
          }
        });
        
        setStatus({
          type: 'success',
          title: 'Resume Created Successfully!',
          description: 'Your resume has been created and is ready to download.'
        });
      } catch (error) {
        setStatus({
          type: 'error',
          title: 'Creation Failed',
          description: error.message || 'Failed to create resume. Please try again.'
        });
      } finally {
        setIsProcessing(false);
        setLoadingStep(0);
      }
    } else {
      // Skip AI enhancement, go directly to template view
      setShowCreateResume(false);
      setEnhancementResult({
        enhanced_resume: manualResumeData,
        changes: null
      });
      setStatus({
        type: 'success',
        title: 'Resume Created Successfully!',
        description: 'Your resume is ready to download.'
      });
    }
  };

  const generateTextPDF = (resumeData, fileName, template = 'modern') => {
    const { jsPDF } = window.jspdf;
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const maxWidth = pageWidth - (margin * 2);
    let yPos = margin;

    // Template-specific configurations
    const templateConfig = {
      modern: {
        headerColor: [37, 99, 235], // Blue
        sectionColor: [37, 99, 235],
        font: 'helvetica',
        useUnderline: true,
        skillSeparator: ', '
      },
      professional: {
        headerColor: [0, 0, 0], // Black
        sectionColor: [0, 0, 0],
        font: 'helvetica',
        useUnderline: false,
        skillSeparator: ' • ',
        leftBorder: true
      },
      creative: {
        headerColor: [147, 51, 234], // Purple
        sectionColor: [147, 51, 234],
        font: 'helvetica',
        useUnderline: false,
        skillSeparator: ', '
      },
      minimal: {
        headerColor: [0, 0, 0], // Black
        sectionColor: [0, 0, 0],
        font: 'times',
        useUnderline: false,
        skillSeparator: ', '
      }
    };

    const config = templateConfig[template] || templateConfig.modern;

    // Helper function to add text with word wrap
    const addText = (text, fontSize, isBold = false, color = [0, 0, 0]) => {
      doc.setFontSize(fontSize);
      doc.setFont(config.font, isBold ? 'bold' : 'normal');
      doc.setTextColor(...color);
      
      const lines = doc.splitTextToSize(text, maxWidth);
      lines.forEach(line => {
        if (yPos > pageHeight - margin) {
          doc.addPage();
          yPos = margin;
        }
        doc.text(line, margin, yPos);
        yPos += fontSize * 0.5;
      });
      yPos += 2;
    };

    const addSection = (title) => {
      yPos += 3;
      doc.setFontSize(template === 'professional' ? 12 : 14);
      doc.setFont(config.font, 'bold');
      doc.setTextColor(...config.sectionColor);
      
      const sectionTitle = template === 'professional' ? title.toUpperCase() : title;
      doc.text(sectionTitle, margin, yPos);
      yPos += 5;
      
      // Add underline for modern template
      if (config.useUnderline) {
        doc.setDrawColor(...config.sectionColor);
        doc.setLineWidth(0.5);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 5;
      } else {
        yPos += 2;
      }
    };

    // Add left border for professional template
    if (config.leftBorder) {
      doc.setDrawColor(50, 50, 50);
      doc.setLineWidth(3);
      doc.line(5, 5, 5, pageHeight - 5);
    }

    // Header - Name and Contact Info
    const { personal_info } = resumeData;
    
    if (template === 'minimal') {
      // Minimal: Left-aligned, simple
      doc.setFontSize(20);
      doc.setFont(config.font, 'normal');
      doc.setTextColor(0, 0, 0);
      doc.text(personal_info?.name || 'Your Name', margin, yPos);
      yPos += 8;
      
      doc.setFontSize(9);
      if (personal_info?.email) {
        doc.text(personal_info.email, margin, yPos);
        yPos += 4;
      }
      if (personal_info?.phone) {
        doc.text(personal_info.phone, margin, yPos);
        yPos += 4;
      }
      if (personal_info?.location) {
        doc.text(personal_info.location, margin, yPos);
        yPos += 4;
      }
      if (personal_info?.linkedin) {
        doc.text('LinkedIn Profile Available', margin, yPos);
        yPos += 4;
      }
      yPos += 3;
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.3);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 8;
    } else if (template === 'professional') {
      // Professional: Large name, grid contact info
      doc.setFontSize(24);
      doc.setFont(config.font, 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text(personal_info?.name || 'Your Name', margin, yPos);
      yPos += 10;
      
      doc.setFontSize(9);
      doc.setFont(config.font, 'normal');
      doc.setTextColor(100, 100, 100);
      const contactItems = [
        personal_info?.email,
        personal_info?.phone,
        personal_info?.location,
        personal_info?.linkedin ? 'LinkedIn Profile' : null
      ].filter(Boolean);
      
      contactItems.forEach((item, idx) => {
        if (idx % 2 === 0) {
          doc.text(item, margin, yPos);
        } else {
          doc.text(item, pageWidth / 2, yPos);
          yPos += 4;
        }
      });
      if (contactItems.length % 2 !== 0) yPos += 4;
      
      yPos += 3;
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.5);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 8;
    } else {
      // Modern & Creative: Centered name, pipe-separated contact
      doc.setFontSize(24);
      doc.setFont(config.font, 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text(personal_info?.name || 'Your Name', pageWidth / 2, yPos, { align: 'center' });
      yPos += 8;

      doc.setFontSize(10);
      doc.setFont(config.font, 'normal');
      const contactInfo = [
        personal_info?.email,
        personal_info?.phone,
        personal_info?.location,
        personal_info?.linkedin ? 'LinkedIn Profile' : null
      ].filter(Boolean).join(' | ');
      doc.text(contactInfo, pageWidth / 2, yPos, { align: 'center' });
      yPos += 10;
    }

    // Professional Summary
    if (resumeData.summary) {
      addSection(template === 'minimal' ? 'Summary' : 'Professional Summary');
      addText(resumeData.summary, 10);
    }

    // Professional Experience
    if (resumeData.experience && resumeData.experience.length > 0) {
      addSection(template === 'minimal' ? 'Experience' : 'Professional Experience');
      resumeData.experience.forEach(exp => {
        // Add left border for professional template sections
        if (config.leftBorder) {
          const sectionStartY = yPos;
          doc.setDrawColor(200, 200, 200);
          doc.setLineWidth(1.5);
          doc.line(margin - 3, sectionStartY - 2, margin - 3, yPos + 20);
        }
        
        // Job Title and Company
        doc.setFontSize(12);
        doc.setFont(config.font, 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text(exp.title, margin, yPos);
        yPos += 5;

        doc.setFontSize(11);
        doc.setTextColor(template === 'professional' ? 50 : 37, template === 'professional' ? 50 : 99, template === 'professional' ? 50 : 235);
        doc.text(exp.company, margin, yPos);
        
        // Duration and Location (right aligned)
        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.text(exp.duration, pageWidth - margin, yPos, { align: 'right' });
        yPos += 5;

        if (exp.location) {
          doc.setFontSize(9);
          doc.text(exp.location, pageWidth - margin, yPos, { align: 'right' });
          yPos += 4;
        }

        // Description bullets
        if (exp.description && exp.description.length > 0) {
          doc.setFontSize(10);
          doc.setTextColor(0, 0, 0);
          doc.setFont(config.font, 'normal');
          exp.description.forEach(desc => {
            const bulletX = margin + 3;
            doc.text('•', bulletX, yPos);
            const lines = doc.splitTextToSize(desc, maxWidth - 6);
            lines.forEach((line, idx) => {
              if (yPos > pageHeight - margin) {
                doc.addPage();
                yPos = margin;
              }
              doc.text(line, bulletX + 4, yPos);
              yPos += 4.5;
            });
          });
        }
        yPos += 3;
      });
    }

    // Projects
    if (resumeData.projects && resumeData.projects.length > 0) {
      addSection(template === 'minimal' ? 'Projects' : 'Key Projects');
      resumeData.projects.forEach(project => {
        if (config.leftBorder) {
          doc.setDrawColor(200, 200, 200);
          doc.setLineWidth(1.5);
          doc.line(margin - 3, yPos - 2, margin - 3, yPos + 15);
        }
        
        doc.setFontSize(12);
        doc.setFont(config.font, 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text(project.title, margin, yPos);
        
        if (project.duration) {
          doc.setFontSize(10);
          doc.setTextColor(100, 100, 100);
          doc.text(project.duration, pageWidth - margin, yPos, { align: 'right' });
        }
        yPos += 5;

        if (project.description) {
          addText(project.description, 10);
        }

        if (project.technologies && project.technologies.length > 0) {
          doc.setFontSize(9);
          doc.setFont(config.font, 'bold');
          doc.text('Technologies: ', margin, yPos);
          doc.setFont(config.font, 'normal');
          doc.text(project.technologies.join(', '), margin + 25, yPos);
          yPos += 5;
        }

        if (project.achievements && project.achievements.length > 0) {
          doc.setFontSize(9);
          doc.setFont(config.font, 'normal');
          project.achievements.forEach(achievement => {
            const bulletX = margin + 3;
            doc.text('•', bulletX, yPos);
            const lines = doc.splitTextToSize(achievement, maxWidth - 6);
            lines.forEach(line => {
              if (yPos > pageHeight - margin) {
                doc.addPage();
                yPos = margin;
              }
              doc.text(line, bulletX + 4, yPos);
              yPos += 4;
            });
          });
        }
        yPos += 3;
      });
    }

    // Skills
    if (resumeData.skills) {
      addSection(template === 'professional' ? 'Core Competencies' : 'Skills');
      const skillCategories = [
        { key: 'technical', label: 'Technical Skills' },
        { key: 'programming', label: 'Programming Languages' },
        { key: 'tools', label: 'Tools & Technologies' },
        { key: 'soft_skills', label: 'Soft Skills' }
      ];

      skillCategories.forEach(category => {
        if (resumeData.skills[category.key] && resumeData.skills[category.key].length > 0) {
          doc.setFontSize(11);
          doc.setFont(config.font, 'bold');
          doc.setTextColor(0, 0, 0);
          doc.text(category.label + ':', margin, yPos);
          yPos += 5;

          doc.setFontSize(10);
          doc.setFont(config.font, 'normal');
          const skillsText = resumeData.skills[category.key].join(config.skillSeparator);
          const lines = doc.splitTextToSize(skillsText, maxWidth);
          lines.forEach(line => {
            if (yPos > pageHeight - margin) {
              doc.addPage();
              yPos = margin;
            }
            doc.text(line, margin, yPos);
            yPos += 4.5;
          });
          yPos += 2;
        }
      });
    }

    // Education
    if (resumeData.education && resumeData.education.length > 0) {
      addSection('Education');
      resumeData.education.forEach(edu => {
        if (config.leftBorder) {
          doc.setDrawColor(200, 200, 200);
          doc.setLineWidth(1.5);
          doc.line(margin - 3, yPos - 2, margin - 3, yPos + 12);
        }
        
        doc.setFontSize(12);
        doc.setFont(config.font, 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text(edu.degree, margin, yPos);
        
        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.text(edu.year, pageWidth - margin, yPos, { align: 'right' });
        yPos += 5;

        doc.setFontSize(11);
        doc.setTextColor(template === 'professional' ? 50 : 37, template === 'professional' ? 50 : 99, template === 'professional' ? 50 : 235);
        doc.text(edu.institution, margin, yPos);
        yPos += 5;

        if (edu.gpa) {
          doc.setFontSize(9);
          doc.setTextColor(100, 100, 100);
          doc.text(`GPA: ${edu.gpa}`, margin, yPos);
          yPos += 4;
        }

        if (edu.relevant_coursework && edu.relevant_coursework.length > 0) {
          doc.setFontSize(9);
          doc.setTextColor(0, 0, 0);
          const coursework = (template === 'professional' ? 'Relevant Coursework: ' : 'Coursework: ') + edu.relevant_coursework.join(', ');
          const lines = doc.splitTextToSize(coursework, maxWidth);
          lines.forEach(line => {
            if (yPos > pageHeight - margin) {
              doc.addPage();
              yPos = margin;
            }
            doc.text(line, margin, yPos);
            yPos += 4;
          });
        }
        yPos += 3;
      });
    }

    // Certifications
    if (resumeData.certifications && resumeData.certifications.length > 0) {
      addSection('Certifications');
      resumeData.certifications.forEach(cert => {
        if (config.leftBorder) {
          doc.setDrawColor(200, 200, 200);
          doc.setLineWidth(1.5);
          doc.line(margin - 3, yPos - 2, margin - 3, yPos + 10);
        }
        
        doc.setFontSize(11);
        doc.setFont(config.font, 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text(cert.name, margin, yPos);
        
        if (cert.year) {
          doc.setFontSize(10);
          doc.setTextColor(100, 100, 100);
          doc.text(cert.year, pageWidth - margin, yPos, { align: 'right' });
        }
        yPos += 5;

        if (cert.issuer) {
          doc.setFontSize(10);
          doc.setTextColor(template === 'professional' ? 50 : 37, template === 'professional' ? 50 : 99, template === 'professional' ? 50 : 235);
          doc.text(cert.issuer, margin, yPos);
          yPos += 5;
        }
        
        if (cert.credential_id && template === 'professional') {
          doc.setFontSize(9);
          doc.setTextColor(100, 100, 100);
          doc.text(`Credential ID: ${cert.credential_id}`, margin, yPos);
          yPos += 4;
        }
      });
    }

    // Save the PDF
    doc.save(fileName);
  };

  const renderEnhancedResume = (resumeData) => {
    if (!resumeData) return null;

    const SelectedTemplate = RESUME_TEMPLATES[selectedTemplate].component;
    return <SelectedTemplate resumeData={resumeData} />;
  };

  // Loading Animation Component
  const renderLoadingAnimation = () => {
    const loadingSteps = [
      { id: 1, text: 'Loading your resume', icon: 'description' },
      { id: 2, text: 'Parsing content', icon: 'find_in_page' },
      { id: 3, text: 'Analyzing job requirements', icon: 'analytics' },
      { id: 4, text: 'Enhancing your resume', icon: 'auto_awesome' },
      { id: 5, text: 'Finalizing', icon: 'check_circle' }
    ];

    const progressPct = Math.round((loadingStep / loadingSteps.length) * 100);

    // Only show the current active step (not completed ones)
    const currentStep = loadingSteps.find(s => s.id === loadingStep) || loadingSteps[loadingSteps.length - 1];
    const isFinalizing = loadingStep >= loadingSteps.length;

    return (
      <div className="fixed inset-0 bg-white z-50 flex">
        {/* Left Side - Clean minimal progress */}
        <div className="w-1/2 flex items-center justify-center bg-gray-50">
          <div className="max-w-sm w-full px-8">
            {/* Icon */}
            <div className="flex justify-center mb-8">
              <div className="w-16 h-16 bg-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
                <Icon name={isFinalizing ? 'check_circle' : currentStep.icon} className="text-white" size={32} />
              </div>
            </div>

            {/* Current step text */}
            <div className="text-center mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-1">
                {isFinalizing ? 'Almost done...' : currentStep.text}
              </h2>
              <p className="text-sm text-gray-500">
                Step {Math.min(loadingStep, loadingSteps.length)} of {loadingSteps.length}
              </p>
            </div>

            {/* Progress bar */}
            <div className="mb-6">
              <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-teal-600 rounded-full transition-all duration-1000 ease-out"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <p className="text-right text-xs text-gray-400 mt-1.5">{progressPct}%</p>
            </div>

            {/* Step indicators - small dots */}
            <div className="flex justify-center gap-2">
              {loadingSteps.map((step) => (
                <div
                  key={step.id}
                  className={`w-2 h-2 rounded-full transition-all duration-500 ${
                    loadingStep >= step.id
                      ? 'bg-teal-600'
                      : loadingStep === step.id - 1
                      ? 'bg-teal-400 animate-pulse'
                      : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Right Side - Resume Preview with Scanner */}
        <div className="w-1/2 bg-white flex items-center justify-center p-8 relative overflow-hidden border-l border-gray-200">
          <div className="max-w-3xl w-full h-[90vh] bg-white rounded-xl shadow-lg overflow-hidden relative border border-gray-200">
            {resumePreviewUrl ? (
              <>
                <div className="w-full h-full overflow-hidden relative">
                  <iframe
                    src={`${resumePreviewUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                    className="w-full h-full re-pdf-viewer"
                    title="Resume Preview"
                    style={{ minHeight: '100%', pointerEvents: 'none', border: 'none' }}
                  />
                </div>

                {/* Scanner line */}
                <div className="absolute inset-0 pointer-events-none">
                  <div className="re-scanner-line"></div>
                </div>

                {/* Scanning badge */}
                <div className="absolute top-4 right-4 bg-teal-600 text-white px-3 py-1.5 rounded-full shadow-md flex items-center gap-2 text-xs font-medium">
                  <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></div>
                  Scanning
                </div>

                {/* Corner brackets */}
                <div className="absolute top-3 left-3 w-6 h-6 border-t-2 border-l-2 border-teal-500 rounded-tl"></div>
                <div className="absolute top-3 right-3 w-6 h-6 border-t-2 border-r-2 border-teal-500 rounded-tr"></div>
                <div className="absolute bottom-3 left-3 w-6 h-6 border-b-2 border-l-2 border-teal-500 rounded-bl"></div>
                <div className="absolute bottom-3 right-3 w-6 h-6 border-b-2 border-r-2 border-teal-500 rounded-br"></div>
              </>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <Icon name="description" className="text-gray-300 mx-auto mb-3" size={48} />
                  <p className="text-gray-400 text-sm">Loading preview...</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <style>{`
          .re-pdf-viewer {
            animation: reAutoScroll 15s ease-in-out infinite;
          }
          @keyframes reAutoScroll {
            0% { transform: translateY(0); }
            50% { transform: translateY(-50%); }
            100% { transform: translateY(0); }
          }
          @keyframes reScan {
            0% { top: -4px; opacity: 0; }
            5% { opacity: 1; }
            95% { opacity: 1; }
            100% { top: 100%; opacity: 0; }
          }
          .re-scanner-line {
            position: absolute;
            left: 0;
            right: 0;
            height: 2px;
            background: linear-gradient(90deg, transparent, rgba(13,148,136,0.4), rgba(13,148,136,1), rgba(13,148,136,0.4), transparent);
            box-shadow: 0 0 12px rgba(13,148,136,0.6);
            animation: reScan 4s ease-in-out infinite;
            z-index: 10;
          }
          .re-scanner-line::before {
            content: '';
            position: absolute;
            left: 0;
            right: 0;
            height: 60px;
            top: -30px;
            background: linear-gradient(180deg, transparent, rgba(13,148,136,0.06), transparent);
          }
        `}</style>
      </div>
    );
  };

  // Render Create Resume Multi-Step Form
  const renderCreateResumeForm = () => {
    const totalSteps = 6;
    const progress = (createResumeStep / totalSteps) * 100;

    return (
      <div className="fixed inset-0 bg-white z-50 overflow-y-auto">
        <LandingHeader />
        
        <div className="max-w-4xl mx-auto py-12 px-4">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => setShowCreateResume(false)}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
            >
              <Icon name="arrow_back" />
              <span>Back to Upload</span>
            </button>
            
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Create Your Resume</h1>
            <p className="text-gray-600">Step {createResumeStep} of {totalSteps}</p>
            
            {/* Progress Bar */}
            <div className="mt-4 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-blue-600 to-purple-600 transition-all duration-500"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>

          {/* Step Content */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
            {/* Step 1: Personal Information */}
            {createResumeStep === 1 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Personal Information</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                    <input
                      type="text"
                      value={manualResumeData.personal_info.name}
                      onChange={(e) => handleManualDataChange('personal_info', 'name', e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="John Doe"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                    <input
                      type="email"
                      value={manualResumeData.personal_info.email}
                      onChange={(e) => handleManualDataChange('personal_info', 'email', e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="john@example.com"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone *</label>
                    <input
                      type="tel"
                      value={manualResumeData.personal_info.phone}
                      onChange={(e) => handleManualDataChange('personal_info', 'phone', e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                    <input
                      type="text"
                      value={manualResumeData.personal_info.location}
                      onChange={(e) => handleManualDataChange('personal_info', 'location', e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="New York, NY"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">LinkedIn Profile</label>
                    <input
                      type="url"
                      value={manualResumeData.personal_info.linkedin}
                      onChange={(e) => handleManualDataChange('personal_info', 'linkedin', e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="https://linkedin.com/in/johndoe"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Professional Summary */}
            {createResumeStep === 2 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Professional Summary</h2>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Summary *
                  </label>
                  <textarea
                    value={manualResumeData.summary}
                    onChange={(e) => setManualResumeData(prev => ({ ...prev, summary: e.target.value }))}
                    rows={6}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Write a brief professional summary highlighting your key skills, experience, and career objectives..."
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    Tip: Keep it concise (2-3 sentences) and focus on your unique value proposition
                  </p>
                </div>
              </div>
            )}

            {/* Step 3: Experience */}
            {createResumeStep === 3 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Work Experience</h2>
                
                {/* Existing Experience List */}
                {manualResumeData.experience.length > 0 && (
                  <div className="space-y-4 mb-6">
                    {manualResumeData.experience.map((exp, index) => (
                      <div key={index} className="p-4 bg-gray-50 rounded-xl">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-semibold text-gray-900">{exp.title}</h3>
                            <p className="text-gray-600">{exp.company} • {exp.duration}</p>
                          </div>
                          <button
                            onClick={() => handleRemoveArrayItem('experience', index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Icon name="delete" size={20} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Add New Experience Form */}
                <div className="p-6 bg-blue-50 rounded-xl space-y-4">
                  <h3 className="font-semibold text-gray-900">Add Experience</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      placeholder="Job Title"
                      className="px-4 py-2 rounded-lg border border-gray-300"
                      value={tempDescInput}
                      onChange={(e) => setTempDescInput(e.target.value)}
                    />
                    <input
                      type="text"
                      placeholder="Company"
                      className="px-4 py-2 rounded-lg border border-gray-300"
                    />
                    <input
                      type="text"
                      placeholder="Duration (e.g., Jan 2020 - Present)"
                      className="px-4 py-2 rounded-lg border border-gray-300"
                    />
                    <input
                      type="text"
                      placeholder="Location"
                      className="px-4 py-2 rounded-lg border border-gray-300"
                    />
                  </div>
                  
                  <textarea
                    placeholder="Job Description (one responsibility per line)"
                    rows={4}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300"
                  />
                  
                  <button
                    onClick={() => {
                      // Add experience logic here
                      setTempDescInput('');
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <Icon name="add" className="inline mr-2" />
                    Add Experience
                  </button>
                </div>
                
                <p className="text-sm text-gray-500">
                  You can skip this step and add it later if needed
                </p>
              </div>
            )}

            {/* Step 4: Education & Skills */}
            {createResumeStep === 4 && (
              <div className="space-y-8">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Education</h2>
                  
                  {manualResumeData.education.length > 0 && (
                    <div className="space-y-4 mb-6">
                      {manualResumeData.education.map((edu, index) => (
                        <div key={index} className="p-4 bg-gray-50 rounded-xl flex justify-between items-start">
                          <div>
                            <h3 className="font-semibold text-gray-900">{edu.degree}</h3>
                            <p className="text-gray-600">{edu.institution} • {edu.year}</p>
                          </div>
                          <button
                            onClick={() => handleRemoveArrayItem('education', index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Icon name="delete" size={20} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Skills</h2>
                  
                  <div className="space-y-4">
                    {['technical', 'programming', 'tools', 'soft_skills'].map((skillType) => (
                      <div key={skillType}>
                        <label className="block text-sm font-medium text-gray-700 mb-2 capitalize">
                          {skillType.replace('_', ' ')}
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            placeholder={`Add ${skillType.replace('_', ' ')} and press Enter`}
                            className="flex-1 px-4 py-2 rounded-lg border border-gray-300"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter' && e.target.value.trim()) {
                                handleManualDataChange('skills', skillType, [
                                  ...manualResumeData.skills[skillType],
                                  e.target.value.trim()
                                ]);
                                e.target.value = '';
                              }
                            }}
                          />
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {manualResumeData.skills[skillType].map((skill, idx) => (
                            <span
                              key={idx}
                              className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm flex items-center gap-2"
                            >
                              {skill}
                              <button
                                onClick={() => {
                                  const newSkills = manualResumeData.skills[skillType].filter((_, i) => i !== idx);
                                  handleManualDataChange('skills', skillType, newSkills);
                                }}
                                className="hover:text-red-600"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step 5: Projects (Optional) */}
            {createResumeStep === 5 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Projects (Optional)</h2>
                
                {manualResumeData.projects.length > 0 && (
                  <div className="space-y-4 mb-6">
                    {manualResumeData.projects.map((project, index) => (
                      <div key={index} className="p-4 bg-gray-50 rounded-xl flex justify-between items-start">
                        <div>
                          <h3 className="font-semibold text-gray-900">{project.title}</h3>
                          <p className="text-gray-600 text-sm">{project.description}</p>
                        </div>
                        <button
                          onClick={() => handleRemoveArrayItem('projects', index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Icon name="delete" size={20} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                <p className="text-sm text-gray-500">
                  Add relevant projects to showcase your skills. You can skip this step if not applicable.
                </p>
              </div>
            )}

            {/* Step 6: Review & Finish */}
            {createResumeStep === 6 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Review Your Resume</h2>
                
                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <h3 className="font-semibold text-gray-900 mb-2">Personal Information</h3>
                    <p className="text-gray-700">{manualResumeData.personal_info.name}</p>
                    <p className="text-gray-600 text-sm">{manualResumeData.personal_info.email}</p>
                  </div>
                  
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <h3 className="font-semibold text-gray-900 mb-2">Summary</h3>
                    <p className="text-gray-700">{manualResumeData.summary || 'No summary added'}</p>
                  </div>
                  
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <h3 className="font-semibold text-gray-900 mb-2">Experience</h3>
                    <p className="text-gray-700">{manualResumeData.experience.length} positions added</p>
                  </div>
                  
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <h3 className="font-semibold text-gray-900 mb-2">Education</h3>
                    <p className="text-gray-700">{manualResumeData.education.length} degrees added</p>
                  </div>
                </div>
                
                <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl">
                  <h3 className="font-semibold text-gray-900 mb-4">What would you like to do next?</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <button
                      onClick={() => handleFinishCreateResume(true)}
                      className="p-6 bg-white rounded-xl border-2 border-blue-600 hover:bg-blue-50 transition-all text-left"
                    >
                      <Icon name="auto_awesome" className="text-blue-600 text-2xl mb-2" />
                      <h4 className="font-semibold text-gray-900 mb-1">Enhance with AI</h4>
                      <p className="text-sm text-gray-600">Let AI optimize your resume for better results</p>
                    </button>
                    
                    <button
                      onClick={() => handleFinishCreateResume(false)}
                      className="p-6 bg-white rounded-xl border-2 border-gray-300 hover:bg-gray-50 transition-all text-left"
                    >
                      <Icon name="check_circle" className="text-green-600 text-2xl mb-2" />
                      <h4 className="font-semibold text-gray-900 mb-1">Continue</h4>
                      <p className="text-sm text-gray-600">Skip AI enhancement and view your resume</p>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between">
            <Button
              onClick={handleCreateResumeBack}
              disabled={createResumeStep === 1}
              variant="outline"
              className="px-6"
            >
              <Icon name="arrow_back" className="mr-2" />
              Back
            </Button>
            
            {createResumeStep < 6 && (
              <Button
                onClick={handleCreateResumeNext}
                className="px-6"
              >
                Next
                <Icon name="arrow_forward" className="ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderChanges = (changes) => {
    if (!changes) return null;

    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6">
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Icon name="auto_awesome" className="text-green-600" />
          What We Enhanced
        </h3>
        <p className="text-gray-600 dark:text-gray-300 mb-6">
          Here's a detailed breakdown of all the improvements made to your resume:
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {changes.added_skills && changes.added_skills.length > 0 && (
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <h4 className="font-semibold text-green-800 dark:text-green-200 mb-2 flex items-center gap-2">
                <Icon name="add_circle" size={18} className="text-green-600" />
                Added Skills ({changes.added_skills.length})
              </h4>
              <ul className="list-disc list-inside space-y-1 text-green-700 dark:text-green-300 text-sm">
                {changes.added_skills.map((skill, i) => (
                  <li key={i}>{skill}</li>
                ))}
              </ul>
            </div>
          )}

          {changes.added_projects && changes.added_projects.length > 0 && (
            <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
              <h4 className="font-semibold text-purple-800 dark:text-purple-200 mb-2 flex items-center gap-2">
                <Icon name="work" size={18} className="text-purple-600" />
                Added Projects ({changes.added_projects.length})
              </h4>
              <ul className="list-disc list-inside space-y-1 text-purple-700 dark:text-purple-300 text-sm">
                {changes.added_projects.map((project, i) => (
                  <li key={i}>{project}</li>
                ))}
              </ul>
            </div>
          )}

          {changes.enhanced_descriptions && changes.enhanced_descriptions.length > 0 && (
            <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
              <h4 className="font-semibold text-orange-800 dark:text-orange-200 mb-2 flex items-center gap-2">
                <Icon name="edit" size={18} className="text-orange-600" />
                Enhanced Descriptions ({changes.enhanced_descriptions.length})
              </h4>
              <ul className="list-disc list-inside space-y-1 text-orange-700 dark:text-orange-300 text-sm">
                {changes.enhanced_descriptions.map((desc, i) => (
                  <li key={i}>{desc}</li>
                ))}
              </ul>
            </div>
          )}

          {changes.added_keywords && changes.added_keywords.length > 0 && (
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-2 flex items-center gap-2">
                <Icon name="label" size={18} className="text-blue-600" />
                Added Keywords ({changes.added_keywords.length})
              </h4>
              <ul className="list-disc list-inside space-y-1 text-blue-700 dark:text-blue-300 text-sm">
                {changes.added_keywords.map((keyword, i) => (
                  <li key={i}>{keyword}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Enhancement Summary */}
        <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
            <Icon name="insights" className="text-blue-600" />
            Enhancement Impact
          </h4>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {(changes.added_skills?.length || 0) + (changes.added_projects?.length || 0)}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Items Added</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {changes.enhanced_descriptions?.length || 0}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Descriptions Enhanced</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {changes.added_keywords?.length || 0}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Keywords Added</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Show loading animation when processing */}
      {isProcessing && renderLoadingAnimation()}

      {/* Print Styles - Hide everything except resume content */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #resume-content, #resume-content * {
            visibility: visible;
          }
          #resume-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            background: white;
          }
          .no-print {
            display: none !important;
          }
        }
      `}</style>
      
      <div className="min-h-screen no-print" style={{ backgroundColor: '#f8f9fa' }}>
        <LandingHeader />
        
        <main className="py-8 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            {/* Header - only show form header when not showing results */}
            {!enhancementResult && (
              <>
                <div className="mb-8">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    AI Resume Enhancer
                  </h1>
                  <p className="text-gray-600">
                    Upload your resume and a job description to get an ATS-optimized version.
                  </p>
                </div>

                <div className="flex items-center gap-4 mb-6">
                  <CreditCostBadge
                    serviceType="resume_enhance"
                    serviceName="Resume Enhancement"
                    size="md"
                  />
                  <button
                    onClick={() => navigate('/profile/setup?mode=manual')}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#0a1929] text-white text-sm rounded-lg hover:bg-[#0d2137] transition-colors"
                  >
                    <Icon name="edit_note" size={18} />
                    <span className="font-medium">Create Resume from Scratch</span>
                  </button>
                </div>
              </>
            )}

          {!enhancementResult ? (
            <>
            {/* Main Form Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              {/* Left Column - Resume & JD Upload */}
              <div className="lg:col-span-2 space-y-6">
                {/* Candidate Resume Card */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                        <Icon name="person" className="text-purple-600" size={18} />
                      </div>
                      <h3 className="font-semibold text-gray-900">Candidate Resume</h3>
                    </div>
                    <label htmlFor="resume" className="flex items-center gap-1 text-purple-600 text-sm font-medium cursor-pointer hover:text-purple-700">
                      <Icon name="upload_file" size={16} />
                      Upload PDF
                    </label>
                  </div>
                  
                  <input
                    type="file"
                    id="resume"
                    accept=".pdf"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  
                  {formData.resume ? (
                    <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                        <Icon name="description" className="text-green-600" size={20} />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-green-800">{formData.resume.name}</p>
                        <p className="text-xs text-green-600">Ready for enhancement</p>
                      </div>
                      <button 
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, resume: null }))}
                        className="text-green-600 hover:text-green-800"
                      >
                        <Icon name="close" size={18} />
                      </button>
                    </div>
                  ) : (
                    <label htmlFor="resume" className="block cursor-pointer">
                      <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center hover:border-purple-300 hover:bg-purple-50/30 transition-all">
                        <p className="text-gray-400 text-sm">
                          Paste the full text of your resume here or drop a file to automatically extract content...
                        </p>
                      </div>
                    </label>
                  )}
                  
                  <div className="flex items-center justify-between mt-3 text-xs text-gray-400">
                    <span>0 WORDS DETECTED</span>
                    <span className="px-2 py-1 bg-purple-100 text-purple-600 rounded font-medium">ATS OPTIMIZED</span>
                  </div>
                </div>

                {/* Job Description Card */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                        <Icon name="work" className="text-purple-600" size={18} />
                      </div>
                      <h3 className="font-semibold text-gray-900">Job Description</h3>
                    </div>
                    <button type="button" className="flex items-center gap-1 text-purple-600 text-sm font-medium hover:text-purple-700">
                      <Icon name="link" size={16} />
                      Import URL
                    </button>
                  </div>
                  
                  <textarea
                    name="jobDescription"
                    value={formData.jobDescription}
                    onChange={handleInputChange}
                    rows={8}
                    className="w-full px-4 py-3 border-0 bg-transparent text-gray-700 placeholder-gray-400 focus:ring-0 focus:outline-none resize-none"
                    placeholder="Paste the target job description or role requirements..."
                    required
                  />
                </div>
              </div>

              {/* Right Column - AI Configuration */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sticky top-6">
                  <div className="flex items-center gap-2 mb-6">
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Icon name="tune" className="text-purple-600" size={18} />
                    </div>
                    <h3 className="font-semibold text-gray-900">AI Configuration</h3>
                  </div>

                  {/* Job Title */}
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      Job Title (Optional)
                    </label>
                    <input
                      type="text"
                      name="jobTitle"
                      value={formData.jobTitle}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent focus:bg-white transition-all"
                      placeholder="e.g., Senior Software Engineer"
                    />
                  </div>

                  {/* Custom Instructions */}
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      Custom Instructions
                    </label>
                    <textarea
                      name="userPrompt"
                      value={formData.userPrompt}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent focus:bg-white transition-all resize-none italic text-gray-600"
                      placeholder="e.g., Focus on technical leadership skills and quantify achievements with metrics. Use a professional corporate tone."
                    />
                  </div>

                  {/* Creativity Level Slider */}
                  <div className="mb-5">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Creativity Level
                      </label>
                      <span className="text-xs font-medium text-purple-600">Precise</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      defaultValue="30"
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                    />
                    <div className="flex justify-between text-xs text-gray-400 mt-1">
                      <span>LITERAL</span>
                      <span>CREATIVE</span>
                    </div>
                  </div>

                  {/* Professionalism Slider */}
                  <div className="mb-5">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Professionalism
                      </label>
                      <span className="text-xs font-medium text-purple-600">High</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      defaultValue="70"
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                    />
                    <div className="flex justify-between text-xs text-gray-400 mt-1">
                      <span>MODERN</span>
                      <span>CONVENTIONAL</span>
                    </div>
                  </div>

                  {/* Audit Focus Areas */}
                  <div className="mb-6">
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                      Audit Focus Areas
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" defaultChecked className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500" />
                        <span className="text-sm text-gray-700">Skill Matching</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" defaultChecked className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500" />
                        <span className="text-sm text-gray-700">Gap Analysis</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500" />
                        <span className="text-sm text-gray-700">Tone Check</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500" />
                        <span className="text-sm text-gray-700">SEO Keywords</span>
                      </label>
                    </div>
                  </div>

                  {/* Status Message */}
                  {status && (
                    <div className={`mb-4 rounded-lg px-3 py-2 text-sm ${
                      status.type === 'success'
                        ? 'bg-green-50 text-green-700 border border-green-200'
                        : 'bg-red-50 text-red-700 border border-red-200'
                    }`}>
                      <p className="font-medium">{status.title}</p>
                      <p className="text-xs mt-0.5">{status.description}</p>
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isProcessing}
                    className="w-full py-3.5 bg-gradient-to-r from-purple-600 to-purple-700 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-purple-800 transition-all shadow-lg shadow-purple-500/25 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isProcessing ? (
                      <>
                        <Icon name="hourglass_empty" className="animate-spin" size={20} />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        Analyze & Audit Resume
                        <Icon name="bolt" size={20} />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* What You'll Get Section - Horizontal Layout */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="auto_awesome" className="text-purple-600" size={24} />
                <h2 className="text-2xl font-bold text-gray-900">What You'll Get</h2>
              </div>
              <p className="text-gray-600 mb-8">
                Our AI analyzes your resume against the job requirements and provides comprehensive enhancements.
              </p>

              {/* Features Grid - Horizontal */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {/* Feature 1 */}
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Icon name="add_circle" className="text-blue-600" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Relevant Skills Added</h3>
                    <p className="text-sm text-gray-500">AI identifies missing skills from the job description and adds them based on your background.</p>
                  </div>
                </div>

                {/* Feature 2 */}
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                    <Icon name="work" className="text-green-600" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Strategic Projects</h3>
                    <p className="text-sm text-gray-500">Creates believable projects that demonstrate the exact skills mentioned in the job posting.</p>
                  </div>
                </div>

                {/* Feature 3 */}
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                    <Icon name="edit" className="text-purple-600" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Enhanced Descriptions</h3>
                    <p className="text-sm text-gray-500">Improves existing experience with quantifiable results and industry keywords.</p>
                  </div>
                </div>

                {/* Feature 4 */}
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                    <Icon name="palette" className="text-orange-600" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Multiple Templates</h3>
                    <p className="text-sm text-gray-500">Choose from 4 professional templates: Modern, Professional, Creative, and Minimal.</p>
                  </div>
                </div>
              </div>

              {/* Success Stories Section */}
              <div className="border-t border-gray-100 pt-6">
                <h3 className="font-semibold text-gray-900 mb-4">Success Stories</h3>
                <div className="flex flex-wrap items-center gap-6">
                  {/* Stats */}
                  <div className="flex gap-4">
                    <div className="text-center px-6 py-3 bg-green-50 rounded-xl">
                      <div className="text-2xl font-bold text-green-600">87%</div>
                      <div className="text-xs text-gray-500">More Interviews</div>
                    </div>
                    <div className="text-center px-6 py-3 bg-blue-50 rounded-xl">
                      <div className="text-2xl font-bold text-blue-600">2.3x</div>
                      <div className="text-xs text-gray-500">Faster Hiring</div>
                    </div>
                  </div>
                  
                  {/* Testimonial */}
                  <div className="flex-1 min-w-[300px] p-4 bg-gray-50 rounded-xl">
                    <p className="text-sm text-gray-700 italic mb-2">
                      "The AI enhanced my resume perfectly for a senior developer role. Got 3 interviews in the first week!"
                    </p>
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                        <span className="text-xs text-white font-bold">S</span>
                      </div>
                      <span className="text-xs text-gray-500">Sarah M., Software Engineer</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            </>
          ) : (
            /* Results - Professional Layout */
            <div>
              {/* Success Banner */}
              <div className="mb-8 bg-[#0a1929] rounded-xl p-6 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <Icon name="check_circle" className="text-emerald-400" size={28} />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Resume Enhanced Successfully</h2>
                    <p className="text-gray-400 text-sm mt-0.5">Your resume has been optimized for ATS systems and tailored to the job description</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setEnhancementResult(null)}
                    className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white border border-gray-600 hover:border-gray-400 rounded-lg transition-colors"
                  >
                    Enhance Another
                  </button>
                  <button
                    onClick={handleDownloadPDF}
                    className="px-5 py-2.5 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors flex items-center gap-2"
                  >
                    <Icon name="download" size={18} />
                    Download PDF
                  </button>
                </div>
              </div>

              {/* Template Selector Bar */}
              <div className="mb-6 bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">Template:</span>
                    <div className="flex gap-2">
                      {Object.entries(RESUME_TEMPLATES).map(([key, template]) => (
                        <button
                          key={key}
                          onClick={() => setSelectedTemplate(key)}
                          className={`px-4 py-1.5 text-sm rounded-full font-medium transition-all ${
                            selectedTemplate === key
                              ? 'bg-[#0a1929] text-white shadow-sm'
                              : 'text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          {template.name}
                        </button>
                      ))}
                    </div>
                  </div>
                  <span className="text-xs text-gray-400">All templates are ATS-optimized</span>
                </div>
              </div>

              {/* Resume Preview */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                <div className="border-b border-gray-100 px-6 py-4 flex items-center justify-between">
                  <h3 className="text-base font-semibold text-gray-900">Resume Preview</h3>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                      Enhanced
                    </span>
                  </div>
                </div>
                <div className="p-6 max-h-[800px] overflow-y-auto bg-gray-50/50">
                  <div id="resume-content" className="bg-white rounded-lg shadow-sm">
                    {renderEnhancedResume(enhancementResult.enhanced_resume)}
                  </div>
                </div>
              </div>

              {/* Enhancement Details */}
              {enhancementResult.changes_made && (
                <div className="mt-8 bg-white rounded-xl border border-gray-200 shadow-sm">
                  <div className="border-b border-gray-100 px-6 py-4">
                    <h3 className="text-base font-semibold text-gray-900">Enhancement Summary</h3>
                  </div>
                  <div className="p-6">
                    {/* Stats Row */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                      <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <div className="text-2xl font-bold text-[#0a1929]">
                          {enhancementResult.changes_made.added_skills?.length || 0}
                        </div>
                        <div className="text-xs text-gray-500 mt-1 font-medium">Skills Added</div>
                      </div>
                      <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <div className="text-2xl font-bold text-[#0a1929]">
                          {enhancementResult.changes_made.added_projects?.length || 0}
                        </div>
                        <div className="text-xs text-gray-500 mt-1 font-medium">Projects Added</div>
                      </div>
                      <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <div className="text-2xl font-bold text-[#0a1929]">
                          {enhancementResult.changes_made.enhanced_descriptions?.length || 0}
                        </div>
                        <div className="text-xs text-gray-500 mt-1 font-medium">Descriptions Enhanced</div>
                      </div>
                      <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <div className="text-2xl font-bold text-[#0a1929]">
                          {enhancementResult.changes_made.added_keywords?.length || 0}
                        </div>
                        <div className="text-xs text-gray-500 mt-1 font-medium">Keywords Added</div>
                      </div>
                    </div>

                    {/* Detail Sections */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {enhancementResult.changes_made.added_skills?.length > 0 && (
                        <div className="p-4 rounded-lg border border-gray-100">
                          <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                            Added Skills
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {enhancementResult.changes_made.added_skills.map((skill, i) => (
                              <span key={i} className="px-3 py-1 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-full border border-emerald-200">
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {enhancementResult.changes_made.added_keywords?.length > 0 && (
                        <div className="p-4 rounded-lg border border-gray-100">
                          <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                            Added Keywords
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {enhancementResult.changes_made.added_keywords.map((keyword, i) => (
                              <span key={i} className="px-3 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full border border-blue-200">
                                {keyword}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {enhancementResult.changes_made.added_projects?.length > 0 && (
                        <div className="p-4 rounded-lg border border-gray-100">
                          <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-purple-500"></span>
                            Added Projects
                          </h4>
                          <ul className="space-y-1.5">
                            {enhancementResult.changes_made.added_projects.map((project, i) => (
                              <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                                <span className="text-purple-400 mt-1 flex-shrink-0">-</span>
                                {project}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {enhancementResult.changes_made.enhanced_descriptions?.length > 0 && (
                        <div className="p-4 rounded-lg border border-gray-100">
                          <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                            Enhanced Descriptions
                          </h4>
                          <ul className="space-y-1.5">
                            {enhancementResult.changes_made.enhanced_descriptions.map((desc, i) => (
                              <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                                <span className="text-amber-400 mt-1 flex-shrink-0">-</span>
                                {desc}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>

      {/* Profile Completion / Credits Modal */}
      {showCreditModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-[#0a1929] to-[#1e3a5f] rounded-2xl shadow-2xl max-w-md w-full p-8 relative border border-blue-500/20">
            {/* Close button */}
            <button
              onClick={() => setShowCreditModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
            >
              <Icon name="close" size={24} />
            </button>

            {!isProfileComplete ? (
              /* Profile Incomplete - Show Complete Profile Message */
              <>
                <div className="text-center mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="person_add" size={40} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">Complete Your Profile</h3>
                  <p className="text-gray-300 text-sm">
                    Get 50 free credits when you complete your profile!
                  </p>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-6 border border-white/20">
                  <div className="flex items-start space-x-3">
                    <Icon name="stars" size={24} className="text-yellow-400 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="text-white font-semibold mb-2">What you'll get:</h4>
                      <ul className="space-y-2 text-sm text-gray-300">
                        <li className="flex items-center space-x-2">
                          <Icon name="check_circle" size={16} className="text-green-400" />
                          <span>50 free credits instantly</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <Icon name="check_circle" size={16} className="text-green-400" />
                          <span>Access to all AI features</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <Icon name="check_circle" size={16} className="text-green-400" />
                          <span>Better job recommendations</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <Button
                    onClick={() => navigate('/candidate/profile/edit')}
                    className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white py-3 rounded-xl font-semibold shadow-lg"
                  >
                    Complete Profile Now
                  </Button>
                  <Button
                    onClick={() => setShowCreditModal(false)}
                    variant="outline"
                    className="w-full border-2 border-white/30 text-white hover:bg-white/10 py-3 rounded-xl"
                  >
                    Maybe Later
                  </Button>
                </div>
              </>
            ) : (
              /* Profile Complete - Redirect to Payment */
              <>
                <div className="text-center mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="account_balance_wallet" size={40} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">Insufficient Credits</h3>
                  <p className="text-gray-300 text-sm">
                    You need {getCost('resume_enhance')} credits for Resume Enhancement
                  </p>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-6 border border-white/20">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-300 text-sm">Available Credits:</span>
                    <span className="text-white font-bold text-lg">{balance}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300 text-sm">Required Credits:</span>
                    <span className="text-orange-400 font-bold text-lg">{getCost('resume_enhance')}</span>
                  </div>
                </div>

                <div className="space-y-3">
                  <Button
                    onClick={() => navigate('/payment')}
                    className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white py-3 rounded-xl font-semibold shadow-lg"
                  >
                    Buy Credits
                  </Button>
                  <Button
                    onClick={() => setShowCreditModal(false)}
                    variant="outline"
                    className="w-full border-2 border-white/30 text-white hover:bg-white/10 py-3 rounded-xl"
                  >
                    Cancel
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default EnhanceResumePage;
